// @ts-nocheck
import moment from 'moment';

import { ClinicalRelationships, ENTITY_PREFIX } from '../../../../constants';
import { formatEntityId, toUtcOffsetDisplay } from '../../../../helpers';
import {
  HCPCSServiceCodes,
  RELAX_CLAIM_VALIDATIONS_PASSWORD,
  RELAX_CLAIM_VALIDATIONS_PASSWORD_MISMATCH_MESSAGE,
  RELAX_CLAIM_VALIDATIONS_PASSWORD_VERIFIED_MESSAGE,
  ServiceCodes,
} from './constants';
import { ClaimStatus } from '../claim-info/constants';
import {
  FORTA_BLUE,
  FORTA_SKY_BLUE_2,
  FORTA_SUCCESS,
  FORTA_SUCCESS_2,
  FORTA_YELLOW,
  FORTA_YELLOW_2,
} from '../../../../utils/colors';

import type { Moment } from 'moment';
import type {
  IClaim,
  IClientAssignment,
  IProcedure,
  IProviderLicenseDetails,
  IProviderPayorCredentialDetails,
  IServiceCode,
} from '../../../../utils/types';

/** True when the user entered a non-empty password that does not match the expected value. */
export const isRelaxClaimValidationsPasswordMismatch = (
  relaxClaimValidationsPassword: string,
): boolean =>
  relaxClaimValidationsPassword.length > 0 &&
  relaxClaimValidationsPassword !== RELAX_CLAIM_VALIDATIONS_PASSWORD;

/** Helper text for the relax-validations password field: empty input shows nothing. */
export const getRelaxClaimValidationsPasswordInfoText = (
  relaxClaimValidationsPassword: string,
): string | undefined => {
  if (relaxClaimValidationsPassword.length === 0) return undefined;
  return relaxClaimValidationsPassword === RELAX_CLAIM_VALIDATIONS_PASSWORD
    ? RELAX_CLAIM_VALIDATIONS_PASSWORD_VERIFIED_MESSAGE
    : RELAX_CLAIM_VALIDATIONS_PASSWORD_MISMATCH_MESSAGE;
};

export const formatClientBayNames = (cohortAssignments: IClientAssignment[]): string =>
  cohortAssignments
    .map((cohort) => cohort.cohort?.name?.toLocaleLowerCase()?.replace('bay ', '') ?? '')
    .filter(Boolean)
    .join(', ');

export const asDefinedOr = (value: any, Or: any) => {
  if (value) return value;
  return Or;
};

export const isCredentialLicenseRequired = (
  clinicalRelationship: string | null | undefined,
): boolean =>
  !clinicalRelationship ||
  [
    ClinicalRelationships.HomeBcba,
    ClinicalRelationships.BobbingBcba,
    ClinicalRelationships.SubstituteBCBA,
  ].includes(clinicalRelationship);

export const getNavigateUrlForProcedures = (
  selectedProcedureCode: ServiceCodes | HCPCSServiceCodes,
  id: string,
) => {
  if (!selectedProcedureCode) return null;
  let url = null;
  switch (selectedProcedureCode) {
    case ServiceCodes.SessionProcedureCode:
    case HCPCSServiceCodes.SessionProcedureCode:
      url = `/sessions/${id}`;
      break;
    case ServiceCodes.ClientSNProcedureCode:
    case HCPCSServiceCodes.ClientSNProcedureCode:
      url = `/client-supervisions/${id}`;
      break;
    case ServiceCodes.ParentSNProcedureCode:
    case HCPCSServiceCodes.ParentSNProcedureCode:
      url = `/parent-supervisions/${id}`;
      break;
    case ServiceCodes.AssessmentCode:
    case HCPCSServiceCodes.AssessmentCode:
      url = `/assessment-note/${id}`;
      break;
    case ServiceCodes.AssistingAssessmentCode:
    case HCPCSServiceCodes.AssistingAssessmentCode:
      url = `/assessment-note/${id}`;
      break;
    case ServiceCodes.GroupSNProcedureCode:
    case HCPCSServiceCodes.GroupSNProcedureCode:
      url = `/group-parent-training/${id}`;
      break;
    default:
      break;
  }
  return url;
};

export const formatSessionId = (
  selectedProcedureCode: ServiceCodes | HCPCSServiceCodes,
  id: string,
) => {
  if (!selectedProcedureCode) return;

  switch (selectedProcedureCode) {
    case ServiceCodes.SessionProcedureCode:
    case HCPCSServiceCodes.SessionProcedureCode:
      return formatEntityId(id, ENTITY_PREFIX.SessionNotes);
    case ServiceCodes.ClientSNProcedureCode:
    case HCPCSServiceCodes.ClientSNProcedureCode:
      return formatEntityId(id, ENTITY_PREFIX.ClientSupervisionNotes);
    case ServiceCodes.ParentSNProcedureCode:
    case HCPCSServiceCodes.ParentSNProcedureCode:
      return formatEntityId(id, ENTITY_PREFIX.ParentSupervisionNotes);
    case ServiceCodes.AssessmentCode:
    case HCPCSServiceCodes.AssessmentCode:
      return formatEntityId(id, ENTITY_PREFIX.ClientAssessmentNote);
    case ServiceCodes.AssistingAssessmentCode:
    case HCPCSServiceCodes.AssistingAssessmentCode:
      return formatEntityId(id, ENTITY_PREFIX.ClientAssessmentNote);
    case ServiceCodes.GroupSNProcedureCode:
    case HCPCSServiceCodes.GroupSNProcedureCode:
      return formatEntityId(id, ENTITY_PREFIX.GroupSupervisionNotes);
    default:
      break;
  }
};

export const getClaimDetailsMessage = (selectedProcedure: IServiceCode) => {
  if (!selectedProcedure) return;

  switch (selectedProcedure.code) {
    case ServiceCodes.SessionProcedureCode:
    case HCPCSServiceCodes.SessionProcedureCode:
      return 'Add session(s) to view claim details';
    case ServiceCodes.ClientSNProcedureCode:
    case HCPCSServiceCodes.ClientSNProcedureCode:
      return 'Add client supervision(s) to view claim details';
    case ServiceCodes.ParentSNProcedureCode:
    case HCPCSServiceCodes.ParentSNProcedureCode:
      return 'Add parent supervision(s) to view claim details';
    case ServiceCodes.AssessmentCode:
    case HCPCSServiceCodes.AssessmentCode:
      return 'Add assessment(s) to view claim details';
    case ServiceCodes.AssistingAssessmentCode:
    case HCPCSServiceCodes.AssistingAssessmentCode:
      return 'Add assisting assessment(s) to view claim details';
    case ServiceCodes.GroupSNProcedureCode:
    case HCPCSServiceCodes.GroupSNProcedureCode:
      return 'Add group supervision(s) to view details';
    default:
      break;
  }
};

export const getSessionsInClaimsData = (claim: IClaim | null) => {
  if (!claim) return [];
  const { bcba, procedures, service_code } = claim;
  return procedures.map((procedure: IProcedure) => {
    return {
      service_code: service_code.code,
      id: procedure.procedure_id,
      date: `${moment(procedure.date_start).format('MM/DD/YYYY')} (${
        procedure.utc_offset_date_start
          ? toUtcOffsetDisplay(asDefinedOr(procedure.utc_offset_date_start, 0))
          : '0:0'
      })`,
      bcba: `${bcba.first_name} ${bcba.last_name}`,
    };
  });
};

export const buildClaimUpdatePaylod = (claim: IClaim, updates: any) => {
  return {
    date_submitted_to_clearinghouse: claim.date_submitted_to_clearinghouse,
    utc_offset_date_submitted_to_clearinghouse: claim.utc_offset_date_submitted_to_clearinghouse,
    clearing_house_claim_id: claim.clearing_house_claim_id,
    reason_for_rejection: claim.reason_for_rejection,
    date_rejected: claim.date_rejected,
    amount_insurance_paid: claim.amount_insurance_paid,
    co_insurance: claim.co_insurance,
    co_pay: claim.co_pay,
    deductible: claim.deductible,
    adjustments: claim.adjustments,
    patient_responsibility: claim.patient_responsibility,
    date_deposit_cleared_in_bank: claim.date_deposit_cleared_in_bank,
    date_patient_paid: claim.date_patient_paid,
    amount_patient_paid: claim.amount_patient_paid,
    is_payment_expected: claim.is_payment_expected,
    submission_method: claim.submission_method,
    date_finalized: claim.date_finalized,
    status: claim.status,
    date_primary_approved: claim.date_primary_approved,
    payor_claim_id: claim.payor_claim_id,
    payor_appeal_tracking_id: claim.payor_appeal_tracking_id,
    amount_paid_to_subscriber: claim.amount_paid_to_subscriber,
    amount_recouped: claim.amount_recouped,
    date_amount_recouped: claim.date_amount_recouped,
    recoupment_probability: claim.recoupment_probability ?? null,
    recoupment_reason: claim.recoupment_reason ?? null,
    secondary_reason_for_rejection: claim.secondary_reason_for_rejection ?? null,
    date_secondary_payor_rejected: claim.date_secondary_payor_rejected ?? null,
    secondary_status: claim.secondary_status ?? null,
    date_secondary_approved: claim.date_secondary_approved ?? null,
    secondary_payor_claim_id: claim.secondary_payor_claim_id ?? null,
    date_secondary_submitted_to_clearinghouse:
      claim.date_secondary_submitted_to_clearinghouse ?? null,
    ...updates,
  };
};

export const getSecondaryClaimStatusByPrimaryClaimStatus = (primaryClaimStatus: string) => {
  switch (primaryClaimStatus) {
    case ClaimStatus.UNBILLABLE:
      return ClaimStatus.UNBILLABLE;
  }
  return null;
};

export const getAuthorTagColor = (clinical_relationship: ClinicalRelationships) => {
  switch (clinical_relationship) {
    case ClinicalRelationships.HomeBcba:
    case ClinicalRelationships.BobbingBcba:
    case ClinicalRelationships.SubstituteBCBA:
      return FORTA_YELLOW_2;
    case ClinicalRelationships.Vbt:
      return FORTA_SKY_BLUE_2;
    default:
      return FORTA_SUCCESS_2;
  }
};

export const getAuthorTagBorder = (clinical_relationship: ClinicalRelationships) => {
  switch (clinical_relationship) {
    case ClinicalRelationships.HomeBcba:
    case ClinicalRelationships.BobbingBcba:
    case ClinicalRelationships.SubstituteBCBA:
      return FORTA_YELLOW;
    case ClinicalRelationships.Vbt:
      return FORTA_BLUE.light1;
    default:
      return FORTA_SUCCESS;
  }
};

export const getAuthorTagName = (clinical_relationship: ClinicalRelationships) => {
  switch (clinical_relationship) {
    case ClinicalRelationships.HomeBcba:
      return 'BCBA';
    case ClinicalRelationships.BobbingBcba:
      return 'Bobbing BCBA';
    case ClinicalRelationships.SubstituteBCBA:
      return 'Substitute BCBA';
    case ClinicalRelationships.Vbt:
      return 'VBT';
    case ClinicalRelationships.Prbt:
      return 'BT';
    default:
      return '';
  }
};

export const isProviderLicenseInStateOnDate = (
  licenseDetails: IProviderLicenseDetails,
  date: Moment,
) => {
  return Boolean(
    licenseDetails.licenses.some((license) => {
      return (
        moment(license.date_start).isBefore(date) &&
        (!license.date_end || moment(license.date_end).isAfter(date))
      );
    }),
  );
};

export const isProviderPayorCredentialsonDate = (
  credentials: IProviderPayorCredentialDetails,
  date: Moment,
) => {
  const isCredentialed = Boolean(
    credentials.credentials.some((credential) => {
      return (
        moment(credential.date_start).isBefore(date) &&
        (!credential.date_end || moment(credential.date_end).isAfter(date))
      );
    }),
  );
  if (credentials.is_medicaid_credential_required && !credentials.is_no_medicaid_state) {
    // Check both credentials and medicaid credentials if not is_no_medicaid_state
    return (
      isCredentialed &&
      Boolean(
        credentials.medicaid_credentials.some((credential) => {
          return (
            moment(credential.date_start).isBefore(date) &&
            (!credential.date_end || moment(credential.date_end).isAfter(date))
          );
        }),
      )
    );
  }
  return isCredentialed;
};
